import { Drawer, Loading } from '@/components';
import { useRecursiveOrganizationFilteredByKey } from '@/features/tenant-manager/organization/_hooks/organization';
import { useForm } from '@/hooks';
import { useMessage } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useCallback, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type z from 'zod';
import {
  useCreateOrganizationTemplate,
  useOrganizationTemplate,
  useUpdateOrganizationTemplate,
} from '../_hooks/organization-template';
import { OrganizationTemplateFormSchema } from '../_models/organization-template';
import { useOrganizationTemplateDrawerState } from './OrganizationTemplateDrawerStateProvider';
import { OrganizationTemplateForm } from './OrganizationTemplateForm';

type FormValues = z.infer<typeof OrganizationTemplateFormSchema>;

// /**
//  * Cascader の階層パスから最後の要素（実際の組織ID）を抽出
//  *
//  * @param cascaderValues - Cascaderの階層パス配列
//  * @returns 組織IDの配列
//  */
// const extractOrganizationIds = (cascaderValues: string[][]): string[] => {
//   return cascaderValues
//     .map((ids) => ids[ids.length - 1])
//     .filter((id): id is string => id !== undefined);
// };

export const OrganizationTemplateFormDrawer: FC = () => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();

  const { drawerState, setDrawerState } = useOrganizationTemplateDrawerState();

  const { mutateAsync: createTemplate } = useCreateOrganizationTemplate();
  const { mutateAsync: updateTemplate } = useUpdateOrganizationTemplate();

  const isOpen = drawerState.open;
  const isEdit = isOpen && drawerState.operation === 'update';

  const templateId = isEdit ? drawerState.templateId : '';

  const onClose = useCallback(() => {
    setDrawerState({ open: false });
  }, [setDrawerState]);

  const template = useOrganizationTemplate(templateId);

  const userOrganizationIds = isEdit
    ? (template.data?.userOrganizationIds ?? [])
    : [];
  const organizationIds = isEdit ? (template.data?.organizationIds ?? []) : [];

  const userOrganizationOptions = useRecursiveOrganizationFilteredByKey({
    params: {
      ids: userOrganizationIds,
    },
    options: {
      separator: undefined,
      filterBy: 'id',
      reverse: true,
    },
    enabled: !!userOrganizationIds.length,
  });

  const initialValues: FormValues = useMemo(
    () => ({
      name: template.data?.name ?? '',
      userOrganizationIds: userOrganizationOptions.data ?? [],
      organizationIds: organizationIds,
    }),
    [template.data, userOrganizationOptions, organizationIds],
  );

  const formMethods = useForm<FormValues>({
    values: initialValues,
    resolver: zodResolver(OrganizationTemplateFormSchema),
  });

  return (
    <FormProvider {...formMethods}>
      <Drawer
        size="large"
        open={isOpen}
        onClose={onClose}
        afterOpenChange={(open) => {
          if (!open) {
            formMethods.reset(initialValues);
          }
        }}
        destroyOnClose
      >
        {isEdit && template.isPending ? (
          <Loading />
        ) : (
          <OrganizationTemplateForm
            onSubmit={async (values) => {
              try {
                const processedValues = {
                  ...values,
                  userOrganizationIds: values.userOrganizationIds.flatMap(
                    (v) => v.at(-1) ?? [],
                  ),
                  organizationIds: values.organizationIds,
                };

                isEdit
                  ? await updateTemplate({ id: templateId, ...processedValues })
                  : await createTemplate(processedValues);

                openMessage(
                  'success',
                  isEdit
                    ? t('message-update-completed')
                    : t('message-create-completed'),
                );
                onClose();
              } catch {
                openMessage(
                  'error',
                  isEdit
                    ? t('message-update-failed')
                    : t('message-create-failed'),
                );
              }
            }}
          />
        )}
      </Drawer>
    </FormProvider>
  );
};
